/**
 * Per-line note runtime (W1-06; R§4.1, F§11, A§7.2): struct-of-arrays over one line's notes in Phira's in-line
 * order, built from the lowered IR line, the tempo map and the line's floor curve.
 *
 * Note seconds come ONLY from here (EngineNotesModule.noteSec/noteEndSec delegate to these arrays): the raw or
 * normalised IR triple is converted with `TempoMap.secAtTriple` (Phira `a + b/c` in f64, then the tempo walk in
 * Phira's operation order), so equal beats written with the same integer part give bit-identical seconds.
 * Stored note fields are f32 like Phira's (heights from the floor curve, x, yOffset, speed, size, alpha); times
 * stay f64.
 */

import type { TempoMap } from '../../time/types';
import type { RGB } from '../../util/types';
import type { LineId, NoteId, SpeedMode } from '../../model/types';
import type { IrLine, IrNote } from '../../ir/types';
import type { EngineCompileContext } from '../types';
import { NOTE_ALPHA_MAX, RPE_HALF_WIDTH, RPE_HEIGHT } from './constants';
import { phiraOrder, phiraSortKey, sectionsOf } from './sortKeys';

const fr = Math.fround;

/** RPE note types (F§11.1). */
export const NOTE_TAP = 1;
export const NOTE_HOLD = 2;
export const NOTE_FLICK = 3;
export const NOTE_DRAG = 4;

/** Id stored for an IR note without a source note (never produced by Lower for editor documents). */
export const NO_NOTE_ID = -1;

/** Values the runtime depends on besides the IR line; a mismatch makes the runtime stale. */
export interface RuntimeKeys {
  readonly tempo: TempoMap;
  readonly tempoEpoch: number;
  readonly maxTime: number;
  readonly speedMode: SpeedMode;
  readonly rpeVersion: number;
}

/**
 * One line's notes in Phira order. Arrays are indexed by the sorted position; `fileIndex[i]` is the position in
 * `IrLine.notes`.
 */
export class LineNotesRt {
  readonly line: LineId;
  readonly ir: IrLine;
  /** Freshness keys; replaced in place when only an irrelevant maxTime change happened. */
  keys: RuntimeKeys;
  readonly n: number;
  /** Source IR notes (sorted order). */
  readonly src: readonly IrNote[];
  readonly fileIndex: Int32Array;
  /** NoteId per note (NO_NOTE_ID when the IR note has no source). */
  readonly id: Int32Array;
  /** RPE type 1 Tap, 2 Hold, 3 Flick, 4 Drag. */
  readonly type: Uint8Array;
  readonly time: Float64Array;
  /** Seconds of the endTime triple (holds: end; others: endTime as written, usually = time). */
  readonly endTime: Float64Array;
  /** Floor position of the line at the hit time (f32). */
  readonly height: Float32Array;
  /** Holds: floor position at the end time (f32); others: = height. */
  readonly endHeight: Float32Array;
  /** positionX / 675 (f32). */
  readonly x: Float32Array;
  /** Raw yOffset in RPE-y px (f32). */
  readonly yOffsetRpe: Float32Array;
  /** yOffset * 2/900 * speed (f32): the stored offset, world units before the aspect division. */
  readonly yOff: Float32Array;
  readonly speed: Float32Array;
  readonly size: Float32Array;
  /** min(alpha, 255) / 255 (f32). */
  readonly alpha: Float32Array;
  /** Time from which the note alpha applies (visibleTime step), -Infinity when always visible. */
  readonly visibleFrom: Float64Array;
  readonly above: Uint8Array;
  readonly fake: Uint8Array;
  /** Real non-hold note (aggressive culling applies). */
  readonly plain: Uint8Array;
  readonly sortKey: Float32Array;
  /** Tint bytes, 3 per note (white when absent). */
  readonly tint: Uint8Array;
  readonly fxTint: readonly (RGB | null)[];
  /** IR hitsound string (undefined = the kind's default sound). */
  readonly hitsound: readonly (string | undefined)[];
  /** NoteId -> sorted index. */
  readonly indexOf: ReadonlyMap<NoteId, number>;
  // Sections (see sortKeys.ts).
  readonly nonPlainAboveEnd: number;
  readonly nonPlainEnd: number;
  readonly groupStart: Int32Array;
  readonly groupEnd: Int32Array;
  readonly groupCount: number;
  readonly aboveGroupCount: number;
  /**
   * Per plain group: whether the sort order is also the order of drawn distance (speed 1, or one yOffset for the
   * whole group). Lets the ghost pass stop scanning a culled tail early.
   */
  readonly groupMonotone: Uint8Array;
  /** Latest time at which a note of the line samples the floor curve (max over hit times and hold ends). */
  readonly latestTime: number;
  /** Multi-highlight flags cache, valid while `mhEpoch` equals the global multi-highlight epoch. */
  mhEpoch = -1;
  readonly mh: Uint8Array;

  constructor(line: LineId, ir: IrLine, keys: RuntimeKeys, fields: RuntimeFields) {
    this.line = line;
    this.ir = ir;
    this.keys = keys;
    this.n = fields.n;
    this.src = fields.src;
    this.fileIndex = fields.fileIndex;
    this.id = fields.id;
    this.type = fields.type;
    this.time = fields.time;
    this.endTime = fields.endTime;
    this.height = fields.height;
    this.endHeight = fields.endHeight;
    this.x = fields.x;
    this.yOffsetRpe = fields.yOffsetRpe;
    this.yOff = fields.yOff;
    this.speed = fields.speed;
    this.size = fields.size;
    this.alpha = fields.alpha;
    this.visibleFrom = fields.visibleFrom;
    this.above = fields.above;
    this.fake = fields.fake;
    this.plain = fields.plain;
    this.sortKey = fields.sortKey;
    this.tint = fields.tint;
    this.fxTint = fields.fxTint;
    this.hitsound = fields.hitsound;
    this.indexOf = fields.indexOf;
    const sections = sectionsOf(fields.n, fields.plain, fields.above, fields.speed);
    this.nonPlainAboveEnd = sections.nonPlainAboveEnd;
    this.nonPlainEnd = sections.nonPlainEnd;
    this.groupStart = sections.groupStart;
    this.groupEnd = sections.groupEnd;
    this.groupCount = sections.groupCount;
    this.aboveGroupCount = sections.aboveGroupCount;
    this.groupMonotone = new Uint8Array(sections.groupCount);
    for (let g = 0; g < sections.groupCount; g++) {
      const start = sections.groupStart[g] ?? 0;
      const end = sections.groupEnd[g] ?? 0;
      let mono = fields.speed[start] === 1;
      if (!mono) {
        mono = true;
        const y0 = fields.yOff[start];
        for (let i = start + 1; i < end; i++) if (fields.yOff[i] !== y0) mono = false;
      }
      this.groupMonotone[g] = mono ? 1 : 0;
    }
    let latest = -Infinity;
    for (let i = 0; i < fields.n; i++) {
      const tt = fields.time[i] ?? 0;
      const te = fields.type[i] === NOTE_HOLD ? (fields.endTime[i] ?? tt) : tt;
      if (tt > latest) latest = tt;
      if (te > latest) latest = te;
    }
    this.latestTime = latest;
    this.mh = new Uint8Array(fields.n);
  }

  isHold(i: number): boolean {
    return this.type[i] === NOTE_HOLD;
  }

  noteId(i: number): NoteId {
    return (this.id[i] ?? NO_NOTE_ID) as NoteId;
  }
}

/** Struct-of-arrays fields in sorted order (constructor input). */
export interface RuntimeFields {
  readonly n: number;
  readonly src: readonly IrNote[];
  readonly fileIndex: Int32Array;
  readonly id: Int32Array;
  readonly type: Uint8Array;
  readonly time: Float64Array;
  readonly endTime: Float64Array;
  readonly height: Float32Array;
  readonly endHeight: Float32Array;
  readonly x: Float32Array;
  readonly yOffsetRpe: Float32Array;
  readonly yOff: Float32Array;
  readonly speed: Float32Array;
  readonly size: Float32Array;
  readonly alpha: Float32Array;
  readonly visibleFrom: Float64Array;
  readonly above: Uint8Array;
  readonly fake: Uint8Array;
  readonly plain: Uint8Array;
  readonly sortKey: Float32Array;
  readonly tint: Uint8Array;
  readonly fxTint: readonly (RGB | null)[];
  readonly hitsound: readonly (string | undefined)[];
  readonly indexOf: ReadonlyMap<NoteId, number>;
}

/** yOffset * 2 / 900 * speed in f32, in Phira's operation order (R§4.1). */
export function storedYOffset(yOffset: number, speed: number): number {
  return fr(fr(fr(fr(yOffset) * 2) / RPE_HEIGHT) * fr(speed));
}

/** Note alpha factor min(alpha, 255) / 255 in f32 (u16 alpha; >= 255 opaque). */
export function noteAlphaFactor(alpha: number): number {
  return fr(Math.min(Math.max(alpha, 0), NOTE_ALPHA_MAX) / NOTE_ALPHA_MAX);
}

/** Start of the visible step: `time - visibleTime` when `visibleTime < time`, else -Infinity (F§11.3). */
export function visibleFromOf(time: number, visibleTime: number): number {
  return visibleTime < time ? time - visibleTime : -Infinity;
}

/** Clamps a tint channel to a byte (IR tints are 0..255 integers; anything else is clamped explicitly). */
function tintByte(v: number | undefined): number {
  if (v === undefined || Number.isNaN(v)) return 255;
  return Math.min(255, Math.max(0, Math.round(v)));
}

/**
 * Builds the runtime of `line` from its IR. Heights come from `ctx.floorAt` at the note seconds; seconds from the
 * raw triples. Sorted with `phiraOrder`.
 */
export function buildLineRuntime(ctx: EngineCompileContext, line: LineId, ir: IrLine, tempoEpoch: number): LineNotesRt {
  const tempo = ctx.tempo;
  const notes = ir.notes;
  const n = notes.length;
  // File-order scratch.
  const time = new Float64Array(n);
  const endTime = new Float64Array(n);
  const height = new Float32Array(n);
  const endHeight = new Float32Array(n);
  const speed = new Float32Array(n);
  const yOff = new Float32Array(n);
  const plain = new Uint8Array(n);
  const above = new Uint8Array(n);
  const key = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const note = notes[i] as IrNote;
    const hold = note.type === NOTE_HOLD;
    const t = tempo.secAtTriple(note.startTime);
    const te = tempo.secAtTriple(note.endTime);
    time[i] = t;
    endTime[i] = te;
    const h = fr(ctx.floorAt(line, t));
    height[i] = h;
    endHeight[i] = hold ? fr(ctx.floorAt(line, te)) : h;
    const sp = fr(note.speed);
    speed[i] = sp;
    const yo = storedYOffset(note.yOffset, note.speed);
    yOff[i] = yo;
    plain[i] = note.isFake === 0 && !hold ? 1 : 0;
    above[i] = note.above === 1 ? 1 : 0;
    key[i] = phiraSortKey(h, yo, sp);
  }
  const order = phiraOrder({ n, plain, above, speed, key });

  const s = {
    src: [] as IrNote[],
    fileIndex: new Int32Array(n),
    id: new Int32Array(n),
    type: new Uint8Array(n),
    time: new Float64Array(n),
    endTime: new Float64Array(n),
    height: new Float32Array(n),
    endHeight: new Float32Array(n),
    x: new Float32Array(n),
    yOffsetRpe: new Float32Array(n),
    yOff: new Float32Array(n),
    speed: new Float32Array(n),
    size: new Float32Array(n),
    alpha: new Float32Array(n),
    visibleFrom: new Float64Array(n),
    above: new Uint8Array(n),
    fake: new Uint8Array(n),
    plain: new Uint8Array(n),
    sortKey: new Float32Array(n),
    tint: new Uint8Array(n * 3),
    fxTint: [] as (RGB | null)[],
    hitsound: [] as (string | undefined)[],
  };
  const indexOf = new Map<NoteId, number>();
  for (let k = 0; k < n; k++) {
    const i = order[k] ?? 0;
    const note = notes[i] as IrNote;
    s.src.push(note);
    s.fileIndex[k] = i;
    const id = note.srcNote ?? NO_NOTE_ID;
    s.id[k] = id;
    if (note.srcNote !== null) indexOf.set(note.srcNote, k);
    s.type[k] = note.type;
    s.time[k] = time[i] ?? 0;
    s.endTime[k] = endTime[i] ?? 0;
    s.height[k] = height[i] ?? 0;
    s.endHeight[k] = endHeight[i] ?? 0;
    s.x[k] = fr(fr(note.positionX) / RPE_HALF_WIDTH);
    s.yOffsetRpe[k] = fr(note.yOffset);
    s.yOff[k] = yOff[i] ?? 0;
    s.speed[k] = speed[i] ?? 0;
    s.size[k] = fr(note.size);
    s.alpha[k] = noteAlphaFactor(note.alpha);
    s.visibleFrom[k] = visibleFromOf(time[i] ?? 0, note.visibleTime);
    s.above[k] = above[i] ?? 0;
    s.fake[k] = note.isFake !== 0 ? 1 : 0;
    s.plain[k] = plain[i] ?? 0;
    s.sortKey[k] = key[i] ?? 0;
    const tint = note.tint;
    s.tint[k * 3] = tintByte(tint?.[0]);
    s.tint[k * 3 + 1] = tintByte(tint?.[1]);
    s.tint[k * 3 + 2] = tintByte(tint?.[2]);
    s.fxTint.push(note.tintHitEffects ?? null);
    s.hitsound.push(note.hitsound);
  }
  const keys: RuntimeKeys = {
    tempo,
    tempoEpoch,
    maxTime: ctx.maxTime,
    speedMode: ctx.pkg.speedMode,
    rpeVersion: ctx.pkg.rpeVersion,
  };
  return new LineNotesRt(line, ir, keys, { n, ...s, indexOf });
}

/**
 * How a runtime relates to the current context: 'fresh' (usable), 'stale' (rebuild). A change of maxTime only
 * matters when a note time or hold end lies at or after the earlier of the two values (the floor curve freezes
 * after maxTime and is unchanged before it).
 */
export function runtimeFreshness(
  rt: LineNotesRt,
  ir: IrLine,
  ctx: EngineCompileContext,
  tempoEpoch: number,
): 'fresh' | 'stale' | 'maxTimeOnly' {
  const k = rt.keys;
  if (rt.ir !== ir || k.tempo !== ctx.tempo || k.tempoEpoch !== tempoEpoch) return 'stale';
  if (k.speedMode !== ctx.pkg.speedMode || k.rpeVersion !== ctx.pkg.rpeVersion) return 'stale';
  if (Object.is(k.maxTime, ctx.maxTime)) return 'fresh';
  const threshold = Math.min(k.maxTime, ctx.maxTime);
  if (Number.isNaN(threshold) || rt.latestTime >= threshold) return 'stale';
  return 'maxTimeOnly';
}
